import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Activity,
  Eye,
  Filter,
  Globe,
  MousePointerClick,
  RefreshCw,
  Smartphone,
  TabletSmartphone,
  Users,
  X,
} from 'lucide-react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  fetchActivityTracking,
  fetchVisitorActivity,
  type ActivityChannel,
  type ActivityProductRow,
  type ActivityTrackingData,
  type ActivityVisitorRow,
  type ActivityVisitorType,
  type VisitorActivityData,
} from '../../../services/MasterAdmin/dailyAnalyticsDashboardService';
import InsightsStatCard from './InsightsStatCard';
import InsightsChartCard, { INSIGHTS_CHART_TOOLTIP } from './InsightsChartCard';
import {
  CHART_COLORS,
  opsFilterClass,
  opsTableWrap,
} from '../operations/operationsUi';

const CHANNEL_OPTIONS: { value: ActivityChannel; label: string }[] = [
  { value: 'all', label: 'All channels' },
  { value: 'web', label: 'Website' },
  { value: 'user_app', label: 'User app' },
  { value: 'plus_app', label: 'Plus app' },
];

const VISITOR_OPTIONS: { value: ActivityVisitorType; label: string }[] = [
  { value: 'all', label: 'All visitors' },
  { value: 'anonymous', label: 'Anonymous visitors' },
  { value: 'unknown', label: 'Unknown users' },
  { value: 'registered', label: 'Logged-in users' },
];

const CHANNEL_LABEL: Record<string, string> = {
  web: 'Website',
  user_app: 'User app',
  plus_app: 'Plus app',
};

type AnalysisView = 'services' | 'pages' | 'events' | 'targets' | 'visitors' | 'unknown';

function visitorKindLabel(type?: string): string {
  if (type === 'unknown') return 'Unknown';
  if (type === 'anonymous') return 'Anonymous';
  return 'Logged-in';
}

function flattenServiceRows(data: ActivityTrackingData | null): Array<{
  key: string;
  label: string;
  detail: string;
  events: number;
  unique_visitors: number;
  unique_anonymous: number;
}> {
  const rows: Array<{
    key: string;
    label: string;
    detail: string;
    events: number;
    unique_visitors: number;
    unique_anonymous: number;
  }> = [];
  const pushGroup = (items: ActivityProductRow[]) => {
    items.forEach((item) => {
      rows.push({
        key: item.key,
        label: item.label,
        detail: item.description || item.group,
        events: item.events,
        unique_visitors: item.unique_visitors,
        unique_anonymous: item.unique_anonymous,
      });
      item.children.forEach((child) => {
        rows.push({
          key: child.key,
          label: `${item.label} · ${child.label}`,
          detail: child.description || 'Breakdown',
          events: child.events,
          unique_visitors: child.unique_visitors,
          unique_anonymous: child.unique_anonymous,
        });
      });
    });
  };
  pushGroup(data?.by_service?.services || []);
  pushGroup(data?.by_service?.features || []);
  if (data?.by_service?.other && data.by_service.other.events > 0) {
    rows.push({
      key: data.by_service.other.key,
      label: data.by_service.other.label,
      detail: data.by_service.other.description || 'Uncategorized',
      events: data.by_service.other.events,
      unique_visitors: data.by_service.other.unique_visitors,
      unique_anonymous: data.by_service.other.unique_anonymous,
    });
  }
  return rows;
}

function formatDuration(sec?: number): string {
  const value = Number(sec || 0);
  if (!value || Number.isNaN(value)) return '—';
  if (value < 60) return `${Math.round(value)}s`;
  const min = Math.floor(value / 60);
  const rem = Math.round(value % 60);
  return rem ? `${min}m ${rem}s` : `${min}m`;
}

function formatDateTick(val: string): string {
  if (!val) return '';
  const parsed = new Date(`${val}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return val;
  return parsed.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

function formatLastSeen(value: string): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function shortVisitorId(id: string): string {
  if (!id) return '—';
  if (id.length <= 22) return id;
  return `${id.slice(0, 10)}…${id.slice(-8)}`;
}

const TableSkeleton: React.FC<{ rows?: number; cols?: number }> = ({ rows = 6, cols = 4 }) => (
  <div className="space-y-2">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="grid gap-3" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
        {Array.from({ length: cols }).map((__, j) => (
          <div key={j} className="h-8 animate-pulse rounded bg-gray-100 dark:bg-gray-700/60" />
        ))}
      </div>
    ))}
  </div>
);

const ActivityTrackingPanel: React.FC<{
  fromDate: Date | null;
  toDate: Date | null;
  lockedProduct?: string;
}> = ({ fromDate, toDate, lockedProduct }) => {
  const [channel, setChannel] = useState<ActivityChannel>('all');
  const [visitorType, setVisitorType] = useState<ActivityVisitorType>('all');
  const [page, setPage] = useState('all');
  const [eventType, setEventType] = useState('all');
  const [targetId, setTargetId] = useState('all');
  const [product, setProduct] = useState(lockedProduct || 'all');
  const [view, setView] = useState<AnalysisView>(lockedProduct ? 'pages' : 'services');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ActivityTrackingData | null>(null);
  const [selectedUnknown, setSelectedUnknown] = useState<ActivityVisitorRow | null>(null);
  const [visitorDetail, setVisitorDetail] = useState<VisitorActivityData | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [openSessionId, setOpenSessionId] = useState<string | null>(null);
  const loadRequestId = useRef(0);

  const load = useCallback(async () => {
    if (!fromDate || !toDate || toDate < fromDate) return;
    const requestId = ++loadRequestId.current;
    setLoading(true);
    setError(null);
    try {
      const result = await fetchActivityTracking({
        from: fromDate,
        to: toDate,
        channel,
        visitorType,
        page,
        eventType,
        targetId,
        product,
      });
      if (requestId !== loadRequestId.current) return;
      setData(result);
    } catch (e: any) {
      if (requestId !== loadRequestId.current) return;
      setError(e?.response?.data?.message || 'Failed to load activity tracking');
    } finally {
      if (requestId === loadRequestId.current) setLoading(false);
    }
  }, [fromDate, toDate, channel, visitorType, page, eventType, targetId, product]);

  useLayoutEffect(() => {
    if (!fromDate || !toDate || toDate < fromDate) return;
    setLoading(true);
  }, [fromDate, toDate, channel, visitorType, page, eventType, targetId, product]);

  useEffect(() => {
    void load();
  }, [load]);

  const summary = data?.summary;
  const serviceRows = useMemo(() => flattenServiceRows(data), [data]);
  const activeUnknownSession = useMemo(() => {
    if (!visitorDetail?.sessions.length) return null;
    return visitorDetail.sessions.find((session) => session.session_id === openSessionId) || visitorDetail.sessions[0];
  }, [visitorDetail, openSessionId]);
  const activeUnknownEvents = useMemo(() => {
    if (!activeUnknownSession) return [];
    return [...activeUnknownSession.activities].sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
    );
  }, [activeUnknownSession]);

  const activeFilterCount = useMemo(() => {
    return [channel !== 'all', visitorType !== 'all', page !== 'all', eventType !== 'all', targetId !== 'all', product !== 'all']
      .filter(Boolean).length;
  }, [channel, visitorType, page, eventType, targetId, product]);

  const clearFilters = () => {
    setChannel('all');
    setVisitorType('all');
    setPage('all');
    setEventType('all');
    setTargetId('all');
    if (!lockedProduct) setProduct('all');
  };

  const selectProduct = (key: string) => {
    setProduct(key);
    setView('pages');
  };

  const openUnknownUser = async (row: ActivityVisitorRow) => {
    if (!fromDate || !toDate) return;
    setSelectedUnknown(row);
    setVisitorDetail(null);
    setDetailError(null);
    setOpenSessionId(null);
    setLoadingDetail(true);
    try {
      const detail = await fetchVisitorActivity({
        visitorId: row.visitor_id,
        from: fromDate,
        to: toDate,
      });
      setVisitorDetail(detail);
      setOpenSessionId(detail.sessions[0]?.session_id || null);
    } catch (err: any) {
      setVisitorDetail(null);
      setDetailError(err?.response?.data?.message || 'Failed to load sessions');
    } finally {
      setLoadingDetail(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Activity filters</h3>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              Compare website, user app, and plus app traffic. Anonymous visitors keep a stable id so repeat visits stay linked.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {activeFilterCount > 0 && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-xs font-semibold text-primary-600 hover:underline"
              >
                Clear {activeFilterCount} filter{activeFilterCount === 1 ? '' : 's'}
              </button>
            )}
            <button
              type="button"
              onClick={() => void load()}
              disabled={loading}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        <div className="mb-4 flex flex-wrap gap-2">
          {CHANNEL_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setChannel(opt.value)}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                channel === opt.value
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              {opt.value === 'web' && <Globe className="h-3.5 w-3.5" />}
              {opt.value === 'user_app' && <Smartphone className="h-3.5 w-3.5" />}
              {opt.value === 'plus_app' && <TabletSmartphone className="h-3.5 w-3.5" />}
              {opt.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
          <label className={opsFilterClass}>
            <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-gray-400">Visitor type</span>
            <select
              value={visitorType}
              onChange={(e) => setVisitorType(e.target.value as ActivityVisitorType)}
              className="w-full bg-transparent text-sm text-gray-900 outline-none dark:text-white"
            >
              {VISITOR_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </label>
          <label className={opsFilterClass}>
            <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-gray-400">Page</span>
            <select
              value={page}
              onChange={(e) => setPage(e.target.value)}
              className="w-full bg-transparent text-sm text-gray-900 outline-none dark:text-white"
            >
              <option value="all">All pages</option>
              {(data?.options.pages || []).map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </label>
          <label className={opsFilterClass}>
            <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-gray-400">Event</span>
            <select
              value={eventType}
              onChange={(e) => setEventType(e.target.value)}
              className="w-full bg-transparent text-sm text-gray-900 outline-none dark:text-white"
            >
              <option value="all">All events</option>
              {(data?.options.events || []).map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </label>
          {!lockedProduct && (
          <label className={opsFilterClass}>
            <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-gray-400">Service / feature</span>
            <select
              value={product}
              onChange={(e) => setProduct(e.target.value)}
              className="w-full bg-transparent text-sm text-gray-900 outline-none dark:text-white"
            >
              <option value="all">All services & features</option>
              <optgroup label="Services">
                {(data?.by_service?.services || []).map((row) => (
                  <option key={row.key} value={row.key}>{row.label}</option>
                ))}
              </optgroup>
              <optgroup label="Features">
                {(data?.by_service?.features || []).map((row) => (
                  <option key={row.key} value={row.key}>{row.label}</option>
                ))}
              </optgroup>
            </select>
          </label>
          )}
          <label className={opsFilterClass}>
            <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-gray-400">Target</span>
            <select
              value={targetId}
              onChange={(e) => setTargetId(e.target.value)}
              className="w-full bg-transparent text-sm text-gray-900 outline-none dark:text-white"
            >
              <option value="all">All targets</option>
              {(data?.options.targets || []).map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:border-red-900/40 dark:bg-red-900/20">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <InsightsStatCard
          label="Events"
          value={Number(summary?.total_events || 0).toLocaleString()}
          sub="Page views, clicks, and assigned events"
          icon={Activity}
          loading={loading}
        />
        <InsightsStatCard
          label="Unique visitors"
          value={Number(summary?.unique_visitors || 0).toLocaleString()}
          sub={`${Number(summary?.unique_anonymous || 0).toLocaleString()} anonymous · ${Number(summary?.unique_unknown || 0).toLocaleString()} unknown · ${Number(summary?.unique_registered || 0).toLocaleString()} logged-in`}
          icon={Users}
          loading={loading}
        />
        <InsightsStatCard
          label="Sessions"
          value={Number(summary?.sessions || 0).toLocaleString()}
          sub="Distinct browsing sessions"
          icon={Eye}
          loading={loading}
        />
        <InsightsStatCard
          label="Avg time on page"
          value={`${Number(summary?.avg_dwell_sec || 0).toFixed(0)}s`}
          sub="From screen time / dwell events"
          icon={MousePointerClick}
          loading={loading}
        />
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <InsightsChartCard
          title="Activity over time"
          className="xl:col-span-2"
          heightClass="h-64"
          actions={loading ? <RefreshCw className="h-4 w-4 animate-spin text-primary-500" /> : null}
        >
          {loading ? (
            <div className="h-full animate-pulse rounded-xl bg-gray-100 dark:bg-gray-700/50" />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data?.trend || []} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-20" vertical={false} />
                <XAxis dataKey="date" tickFormatter={formatDateTick} tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip
                  contentStyle={INSIGHTS_CHART_TOOLTIP}
                  labelFormatter={(label) => formatDateTick(String(label))}
                />
                <Area type="monotone" dataKey="events" name="Events" stroke={CHART_COLORS.primary} fill={CHART_COLORS.primary} fillOpacity={0.15} strokeWidth={2} />
                <Area type="monotone" dataKey="unique_anonymous" name="Anonymous visitors" stroke={CHART_COLORS.purple} fill={CHART_COLORS.purple} fillOpacity={0.08} strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </InsightsChartCard>

        <InsightsChartCard title="By channel" heightClass="h-64">
          {loading ? (
            <div className="h-full animate-pulse rounded-xl bg-gray-100 dark:bg-gray-700/50" />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={(data?.by_channel || []).map((row) => ({ ...row, label: CHANNEL_LABEL[row.channel] || row.channel }))} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-20" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip contentStyle={INSIGHTS_CHART_TOOLTIP} />
                <Bar dataKey="events" name="Events" fill={CHART_COLORS.primary} radius={[4, 4, 0, 0]} barSize={28} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </InsightsChartCard>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-400" />
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Event analysis</h3>
          </div>
          <div className="flex flex-wrap gap-1 rounded-lg bg-gray-100 p-1 dark:bg-gray-900">
            {([
              { id: 'services', label: 'By service', hide: Boolean(lockedProduct) },
              { id: 'pages', label: 'By page', hide: false },
              { id: 'events', label: 'By event', hide: false },
              { id: 'targets', label: 'By target', hide: false },
              { id: 'visitors', label: 'Visitors', hide: false },
              { id: 'unknown', label: 'Unknown users', hide: false },
            ] as const).filter((opt) => !opt.hide).map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setView(opt.id)}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${
                  view === opt.id
                    ? 'bg-white text-primary-600 shadow-sm dark:bg-gray-800'
                    : 'text-gray-500 hover:text-gray-800 dark:hover:text-gray-200'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        {view === 'visitors' && (
          <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
            Each row is one visitor. Events are that visitor’s real actions only — not a site-wide total.
          </p>
        )}
        {view === 'unknown' && (
          <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
            Website guests who are not logged in. Click an unknown user to open their sessions and every event in each session.
          </p>
        )}

        {loading ? (
          <TableSkeleton rows={7} cols={4} />
        ) : view === 'services' ? (
          <div className={opsTableWrap}>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-[10px] font-black uppercase tracking-widest text-gray-400 dark:border-gray-700">
                  <th className="px-3 py-3">Service / feature</th>
                  <th className="px-3 py-3">Events</th>
                  <th className="px-3 py-3">Visitors</th>
                  <th className="px-3 py-3">Anonymous</th>
                </tr>
              </thead>
              <tbody>
                {serviceRows.length === 0 ? (
                  <tr><td colSpan={4} className="px-3 py-10 text-center text-gray-400">No service activity in this range.</td></tr>
                ) : (
                  serviceRows.map((row) => (
                    <tr
                      key={row.key}
                      className="cursor-pointer border-b border-gray-50 hover:bg-gray-50 dark:border-gray-700/40 dark:hover:bg-gray-700/30"
                      onClick={() => selectProduct(row.key)}
                    >
                      <td className="px-3 py-3">
                        <p className="font-semibold text-gray-800 dark:text-gray-100">{row.label}</p>
                        <p className="text-[11px] text-gray-400">{row.detail}</p>
                      </td>
                      <td className="px-3 py-3 font-bold">{row.events.toLocaleString()}</td>
                      <td className="px-3 py-3">{row.unique_visitors.toLocaleString()}</td>
                      <td className="px-3 py-3">{row.unique_anonymous.toLocaleString()}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : view === 'pages' ? (
          <div className={opsTableWrap}>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-[10px] font-black uppercase tracking-widest text-gray-400 dark:border-gray-700">
                  <th className="px-3 py-3">Page</th>
                  <th className="px-3 py-3">Events</th>
                  <th className="px-3 py-3">Visitors</th>
                  <th className="px-3 py-3">Anonymous</th>
                </tr>
              </thead>
              <tbody>
                {(data?.by_page || []).length === 0 ? (
                  <tr><td colSpan={4} className="px-3 py-10 text-center text-gray-400">No page activity in this range.</td></tr>
                ) : (
                  (data?.by_page || []).map((row) => (
                    <tr
                      key={row.page}
                      className="cursor-pointer border-b border-gray-50 hover:bg-gray-50 dark:border-gray-700/40 dark:hover:bg-gray-700/30"
                      onClick={() => setPage(row.page)}
                    >
                      <td className="px-3 py-3">
                        <p className="font-semibold text-gray-800 dark:text-gray-100">{row.label}</p>
                        <p className="text-[11px] text-gray-400">{row.page}</p>
                      </td>
                      <td className="px-3 py-3 font-bold">{row.events.toLocaleString()}</td>
                      <td className="px-3 py-3">{row.unique_visitors.toLocaleString()}</td>
                      <td className="px-3 py-3">{row.unique_anonymous.toLocaleString()}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : view === 'events' ? (
          <div className={opsTableWrap}>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-[10px] font-black uppercase tracking-widest text-gray-400 dark:border-gray-700">
                  <th className="px-3 py-3">Event</th>
                  <th className="px-3 py-3">Events</th>
                  <th className="px-3 py-3">Visitors</th>
                  <th className="px-3 py-3">Anonymous</th>
                </tr>
              </thead>
              <tbody>
                {(data?.by_event || []).length === 0 ? (
                  <tr><td colSpan={4} className="px-3 py-10 text-center text-gray-400">No events in this range.</td></tr>
                ) : (
                  (data?.by_event || []).map((row) => (
                    <tr
                      key={row.event_type}
                      className="cursor-pointer border-b border-gray-50 hover:bg-gray-50 dark:border-gray-700/40 dark:hover:bg-gray-700/30"
                      onClick={() => setEventType(row.event_type)}
                    >
                      <td className="px-3 py-3">
                        <p className="font-semibold text-gray-800 dark:text-gray-100">{row.label}</p>
                        <p className="text-[11px] text-gray-400">{row.event_type}</p>
                      </td>
                      <td className="px-3 py-3 font-bold">{row.events.toLocaleString()}</td>
                      <td className="px-3 py-3">{row.unique_visitors.toLocaleString()}</td>
                      <td className="px-3 py-3">{row.unique_anonymous.toLocaleString()}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : view === 'targets' ? (
          <div className={opsTableWrap}>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-[10px] font-black uppercase tracking-widest text-gray-400 dark:border-gray-700">
                  <th className="px-3 py-3">Target</th>
                  <th className="px-3 py-3">Events</th>
                  <th className="px-3 py-3">Visitors</th>
                  <th className="px-3 py-3">Type</th>
                </tr>
              </thead>
              <tbody>
                {(data?.by_target || []).length === 0 ? (
                  <tr><td colSpan={4} className="px-3 py-10 text-center text-gray-400">No target activity in this range.</td></tr>
                ) : (
                  (data?.by_target || []).map((row) => (
                    <tr
                      key={`${row.target_type}-${row.target_id}`}
                      className="cursor-pointer border-b border-gray-50 hover:bg-gray-50 dark:border-gray-700/40 dark:hover:bg-gray-700/30"
                      onClick={() => setTargetId(row.target_id)}
                    >
                      <td className="px-3 py-3">
                        <p className="font-semibold text-gray-800 dark:text-gray-100">{row.label}</p>
                        <p className="text-[11px] text-gray-400">{row.target_id}</p>
                      </td>
                      <td className="px-3 py-3 font-bold">{row.events.toLocaleString()}</td>
                      <td className="px-3 py-3">{row.unique_visitors.toLocaleString()}</td>
                      <td className="px-3 py-3 text-xs uppercase text-gray-500">{row.target_type}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : view === 'unknown' ? (
          <div className="space-y-6">
            <div className={opsTableWrap}>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-[10px] font-black uppercase tracking-widest text-gray-400 dark:border-gray-700">
                    <th className="px-3 py-3">Unknown user</th>
                    <th className="px-3 py-3">Events</th>
                    <th className="px-3 py-3">Pages</th>
                    <th className="px-3 py-3">Last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.unknown_visitors || []).length === 0 ? (
                    <tr><td colSpan={4} className="px-3 py-10 text-center text-gray-400">No unknown website activity in this range.</td></tr>
                  ) : (
                    (data?.unknown_visitors || []).map((row) => (
                      <tr
                        key={row.visitor_id}
                        className="cursor-pointer border-b border-gray-50 hover:bg-gray-50 dark:border-gray-700/40 dark:hover:bg-gray-700/30"
                        onClick={() => void openUnknownUser(row)}
                      >
                        <td className="px-3 py-3">
                          <p className="font-semibold text-gray-800 dark:text-gray-100">Unknown user</p>
                          <p className="font-mono text-[11px] text-gray-400" title={row.visitor_id}>
                            {shortVisitorId(row.visitor_id)} · {CHANNEL_LABEL[row.channel] || row.channel}
                          </p>
                        </td>
                        <td className="px-3 py-3 font-bold">{Number(row.events || 0).toLocaleString()}</td>
                        <td className="px-3 py-3">{Number(row.pages || 0).toLocaleString()}</td>
                        <td className="px-3 py-3">
                          <p>{formatLastSeen(row.last_seen)}</p>
                          <p className="text-[11px] text-gray-400">{row.last_page_label || '—'}</p>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className={opsTableWrap}>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-[10px] font-black uppercase tracking-widest text-gray-400 dark:border-gray-700">
                    <th className="px-3 py-3">Page</th>
                    <th className="px-3 py-3">Events</th>
                    <th className="px-3 py-3">Visitors</th>
                    <th className="px-3 py-3">Anonymous</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.unknown_pages || []).length === 0 ? (
                    <tr><td colSpan={4} className="px-3 py-10 text-center text-gray-400">No unknown page activity in this range.</td></tr>
                  ) : (
                    (data?.unknown_pages || []).map((row) => (
                      <tr
                        key={row.page}
                        className="cursor-pointer border-b border-gray-50 hover:bg-gray-50 dark:border-gray-700/40 dark:hover:bg-gray-700/30"
                        onClick={() => setPage(row.page)}
                      >
                        <td className="px-3 py-3">
                          <p className="font-semibold text-gray-800 dark:text-gray-100">{row.label}</p>
                          <p className="text-[11px] text-gray-400">{row.page}</p>
                        </td>
                        <td className="px-3 py-3 font-bold">{row.events.toLocaleString()}</td>
                        <td className="px-3 py-3">{row.unique_visitors.toLocaleString()}</td>
                        <td className="px-3 py-3">{row.unique_anonymous.toLocaleString()}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className={opsTableWrap}>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-[10px] font-black uppercase tracking-widest text-gray-400 dark:border-gray-700">
                  <th className="px-3 py-3">Visitor</th>
                  <th className="px-3 py-3">Events</th>
                  <th className="px-3 py-3">Channel</th>
                  <th className="px-3 py-3">Last page</th>
                </tr>
              </thead>
              <tbody>
                {(data?.recent_visitors || []).length === 0 ? (
                  <tr><td colSpan={4} className="px-3 py-10 text-center text-gray-400">No visitors in this range.</td></tr>
                ) : (
                  (data?.recent_visitors || []).map((row) => (
                    <tr key={row.visitor_id} className="border-b border-gray-50 dark:border-gray-700/40">
                      <td className="px-3 py-3">
                        <p className="font-semibold text-gray-800 dark:text-gray-100">{visitorKindLabel(row.visitor_type)}</p>
                        <p className="font-mono text-[11px] text-gray-400" title={row.visitor_id}>{shortVisitorId(row.visitor_id)}</p>
                      </td>
                      <td className="px-3 py-3 font-bold">{Number(row.events || 0).toLocaleString()}</td>
                      <td className="px-3 py-3">{CHANNEL_LABEL[row.channel] || row.channel}</td>
                      <td className="px-3 py-3">
                        <p>{row.last_page_label || '—'}</p>
                        <p className="text-[11px] text-gray-400">{formatLastSeen(row.last_seen)}</p>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedUnknown && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setSelectedUnknown(null)}>
          <div
            className="flex h-[86vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-gray-800"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex shrink-0 items-start justify-between border-b border-gray-100 px-5 py-4 dark:border-gray-700">
              <div className="min-w-0 pr-4">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Unknown user</p>
                <h4 className="mt-1 truncate font-mono text-sm font-semibold text-gray-900 dark:text-white" title={selectedUnknown.visitor_id}>
                  {selectedUnknown.visitor_id}
                </h4>
                <p className="mt-1 text-xs text-gray-500">
                  {CHANNEL_LABEL[selectedUnknown.channel] || selectedUnknown.channel}
                  {' · '}
                  {Number(selectedUnknown.events || 0).toLocaleString()} events
                  {selectedUnknown.pages ? ` · ${selectedUnknown.pages} pages` : ''}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedUnknown(null)}
                className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            {loadingDetail ? (
              <div className="grid min-h-0 flex-1 grid-cols-1 md:grid-cols-[280px_1fr]">
                <div className="space-y-2 border-b border-gray-100 p-4 dark:border-gray-700 md:border-b-0 md:border-r">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="h-16 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-700/50" />
                  ))}
                </div>
                <div className="space-y-2 p-4">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <div key={i} className="h-10 animate-pulse rounded bg-gray-100 dark:bg-gray-700/50" />
                  ))}
                </div>
              </div>
            ) : detailError ? (
              <p className="flex flex-1 items-center justify-center p-8 text-sm text-red-500">{detailError}</p>
            ) : !visitorDetail || visitorDetail.sessions.length === 0 ? (
              <p className="flex flex-1 items-center justify-center p-8 text-sm text-gray-400">No sessions found for this unknown user.</p>
            ) : (
              <div className="grid min-h-0 flex-1 grid-cols-1 md:grid-cols-[280px_1fr]">
                <div className="min-h-0 overflow-y-auto border-b border-gray-100 dark:border-gray-700 md:border-b-0 md:border-r">
                  <div className="sticky top-0 z-10 border-b border-gray-100 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Sessions</p>
                    {Number(visitorDetail.total_sessions || 0) > visitorDetail.sessions.length && (
                      <p className="mt-1 text-[11px] text-gray-500">
                        Latest {visitorDetail.sessions.length} of {Number(visitorDetail.total_sessions).toLocaleString()}
                      </p>
                    )}
                  </div>
                  <div className="p-2">
                    {visitorDetail.sessions.map((session, index) => {
                      const selected = openSessionId === session.session_id;
                      return (
                        <button
                          key={session.session_id}
                          type="button"
                          onClick={() => setOpenSessionId(session.session_id)}
                          className={`mb-1 w-full rounded-xl px-3 py-3 text-left transition ${
                            selected
                              ? 'bg-primary-50 ring-1 ring-primary-200 dark:bg-primary-900/20 dark:ring-primary-800'
                              : 'hover:bg-gray-50 dark:hover:bg-gray-700/40'
                          }`}
                        >
                          <p className={`text-sm font-semibold ${selected ? 'text-primary-700 dark:text-primary-300' : 'text-gray-900 dark:text-white'}`}>
                            {index === 0 ? 'Latest session' : `Session ${index + 1}`}
                          </p>
                          <p className="mt-0.5 text-xs text-gray-500">
                            {formatLastSeen(session.ended_at || session.started_at)}
                          </p>
                          <p className="mt-1 text-[11px] text-gray-400">
                            {session.events} events · {session.pages} pages
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="min-h-0 overflow-y-auto">
                  {activeUnknownSession && (
                    <>
                      <div className="sticky top-0 z-10 border-b border-gray-100 bg-white px-5 py-3 dark:border-gray-700 dark:bg-gray-800">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Events</p>
                        <p className="mt-1 text-sm font-semibold text-gray-900 dark:text-white">
                          {formatLastSeen(activeUnknownSession.started_at)}
                          {activeUnknownSession.ended_at && activeUnknownSession.ended_at !== activeUnknownSession.started_at
                            ? ` – ${formatLastSeen(activeUnknownSession.ended_at)}`
                            : ''}
                        </p>
                        <p className="text-xs text-gray-500">Latest first · {activeUnknownEvents.length} events</p>
                      </div>
                      <div className={opsTableWrap}>
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-100 text-left text-[10px] font-black uppercase tracking-widest text-gray-400 dark:border-gray-700">
                              <th className="px-4 py-3">Time</th>
                              <th className="px-4 py-3">Event</th>
                              <th className="px-4 py-3">Page</th>
                            </tr>
                          </thead>
                          <tbody>
                            {activeUnknownEvents.length === 0 ? (
                              <tr>
                                <td colSpan={3} className="px-4 py-10 text-center text-gray-400">No events in this session.</td>
                              </tr>
                            ) : (
                              activeUnknownEvents.map((item, activityIdx) => (
                                <tr key={`${activeUnknownSession.session_id}-${activityIdx}`} className="border-b border-gray-50 dark:border-gray-700/40">
                                  <td className="whitespace-nowrap px-4 py-2.5 text-xs text-gray-500">{formatLastSeen(item.timestamp)}</td>
                                  <td className="px-4 py-2.5">
                                    <p className="font-medium text-gray-800 dark:text-gray-100">{item.event_label}</p>
                                    <p className="text-[11px] text-gray-400">{item.event_type}</p>
                                  </td>
                                  <td className="px-4 py-2.5 text-xs text-gray-600 dark:text-gray-300">{item.page_label || '—'}</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityTrackingPanel;
